// frontend/src/pages/MyPortfolioTab.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Card,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  CircularProgress,
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import {
  getFacultyAbstracts,
  addAbstract,
  deleteAbstract,
  updateAbstract,
  addInterest,
  deleteInterest,
  getFacultySummary,
  updateFacultySummary,
  regenerateFacultySummary,
  getFacultyInterests,
  revectorizeFacultyVector,
} from '../../services/api';
import EditSummaryModal from './modals/EditSummaryModal';
import AddAbstractModal from './modals/AddAbstractModal';
import EditAbstractModal from './modals/EditAbstractModal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function MyPortfolioTab() {
  const [abstracts, setAbstracts] = useState([]);
  const [facultyInterests, setFacultyInterests] = useState([]);
  const [summary, setSummary] = useState('');
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);
  const [isAbstractModalOpen, setIsAbstractModalOpen] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  // New loading states for additional operations
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const [isSavingInterest, setIsSavingInterest] = useState(false);
  const [isDeletingInterest, setIsDeletingInterest] = useState(false);
  const [isAddingAbstractLoading, setIsAddingAbstractLoading] = useState(false);
  const [isEditingAbstractLoading, setIsEditingAbstractLoading] = useState(false);
  const [isDeletingAbstractLoading, setIsDeletingAbstractLoading] = useState(false);

  // For inline interest addition
  const [isAddingInterest, setIsAddingInterest] = useState(false);
  const [newInterest, setNewInterest] = useState('');

  // For delete confirmation (interests)
  const [interestToDelete, setInterestToDelete] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  // For editing abstracts
  const [abstractToEdit, setAbstractToEdit] = useState(null);
  const [isEditAbstractModalOpen, setIsEditAbstractModalOpen] = useState(false);

  // For delete confirmation (publications)
  const [publicationToDelete, setPublicationToDelete] = useState(null);
  const [isConfirmDeletePublicationOpen, setIsConfirmDeletePublicationOpen] = useState(false);

  // State for triggering Fade transitions
  const [fadeIn, setFadeIn] = useState(false);

  // Trigger fade in when component mounts
  useEffect(() => {
    setFadeIn(true);
  }, []);

  useEffect(() => {
    setIsSummaryLoading(true);
    Promise.all([
      getFacultyAbstracts(),
      getFacultySummary(),
      getFacultyInterests(),
    ])
      .then(([abstractsData, summaryData, interestsData]) => {
        setAbstracts(abstractsData.abstracts);
        setSummary(summaryData.summary || '');
        setFacultyInterests(interestsData.interests || []);
      })
      .catch(() => {
        setAbstracts([]);
        setSummary('');
        setFacultyInterests([]);
      })
      .finally(() => setIsSummaryLoading(false));
  }, []);  // empty dependency array so this runs on mount

  // Save Handlers
  const handleSaveSummary = async (newSummary) => {
    setIsSavingSummary(true);
    try {
      await updateFacultySummary(newSummary);
      setSummary(newSummary);
      // Trigger revectorization after updating summary
      const vectorResponse = await revectorizeFacultyVector();
      console.log(vectorResponse.message, vectorResponse.output);
    } catch (error) {
      alert('Failed to update summary and revectorize.');
    } finally {
      setIsSavingSummary(false);
    }
  };

  // New handler for generating summary
  const handleGenerateSummary = async () => {
    setIsGeneratingSummary(true);
    try {
      const data = await regenerateFacultySummary();
      const newSummary = data.summary || data;
      // The summary is already saved in the backend by the regenerateFacultySummary API call
      // No need to call updateFacultySummary again
      
      // Update the UI state with the new summary
      setSummary(newSummary);
      // Trigger revectorization after generating summary
      const vectorResponse = await revectorizeFacultyVector();
      console.log(vectorResponse.message, vectorResponse.output);
    } catch (error) {
      console.error('Error generating summary:', error);
      alert('Failed to generate summary and revectorize.');
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleSaveInterest = async () => {
    if (newInterest.trim()) {
      setIsSavingInterest(true);
      try {
        const data = await addInterest(newInterest);
        setFacultyInterests(data.interests || []);
        setNewInterest('');
        setIsAddingInterest(false);
      } catch (error) {
        alert('Failed to add interest.');
      } finally {
        setIsSavingInterest(false);
      }
    }
  };

  const handleAddAbstract = async ({ title, content }) => {
    setIsAddingAbstractLoading(true);
    try {
      const data = await addAbstract({ title, content });
      setAbstracts([...abstracts, data]);
    } catch (error) {
      alert('Failed to add abstract.');
    } finally {
      setIsAddingAbstractLoading(false);
    }
  };

  // Delete a publication after confirmation.
  const handleConfirmDeletePublication = async () => {
    if (publicationToDelete) {
      // Close the confirmation dialog immediately
      setIsConfirmDeletePublicationOpen(false);
      setIsDeletingAbstractLoading(true);
      try {
        await deleteAbstract(publicationToDelete.id);
        setAbstracts(abstracts.filter((a) => a.id !== publicationToDelete.id));
        setPublicationToDelete(null);
      } catch (error) {
        alert('Failed to delete abstract.');
        setPublicationToDelete(null);
      } finally {
        setIsDeletingAbstractLoading(false);
      }
    }
  };

  // When clicking the Delete button next to an interest.
  const handleRequestDeleteInterest = (interest) => {
    setInterestToDelete(interest);
    setIsConfirmDeleteOpen(true);
  };

  // Confirm deletion of an interest.
  const handleConfirmDeleteInterest = async () => {
    if (interestToDelete) {
      // Close the confirmation dialog immediately
      setIsConfirmDeleteOpen(false);
      setIsDeletingInterest(true);
      try {
        const data = await deleteInterest(interestToDelete);
        setFacultyInterests(data.interests || []);
        setInterestToDelete(null);
      } catch (error) {
        alert('Failed to delete interest.');
        setInterestToDelete(null);
      } finally {
        setIsDeletingInterest(false);
      }
    }
  };

  // Cancel deletion for interests.
  const handleCancelDeleteInterest = () => {
    setIsConfirmDeleteOpen(false);
  };

  // Handle editing abstracts.
  const handleEditAbstract = async (updatedAbstract) => {
    setIsEditingAbstractLoading(true);
    try {
      const data = await updateAbstract(updatedAbstract.id, {
        title: updatedAbstract.title,
        content: updatedAbstract.content,
      });
      setAbstracts((prevAbstracts) =>
        prevAbstracts.map((item) => (item.id === data.id ? data : item))
      );
    } catch (error) {
      alert('Failed to update abstract.');
    } finally {
      setIsEditingAbstractLoading(false);
    }
  };

  // Combined loading state for the Backdrop
  const isAnyOperationLoading =
    isGeneratingSummary ||
    isSavingSummary ||
    isSavingInterest ||
    isDeletingInterest ||
    isAddingAbstractLoading ||
    isEditingAbstractLoading ||
    isDeletingAbstractLoading;

  return (
    <Box sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
        My Portfolio
      </Typography>

      {/* Faculty Summary Card wrapped in Fade */}
      <Fade in={fadeIn} timeout={500}>
        <Card elevation={3} sx={{ p: 2 }}>
          <Typography variant="h5">Faculty Summary</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <InfoOutlinedIcon color="primary" sx={{ marginRight: 1 }} />
            <Typography variant="body1" color="textSecondary">
              Generated summaries are based off your interests and publications.
            </Typography>
          </Box>
          {isSummaryLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 100,
                my: 2,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
              {summary || 'No summary available. Click "Edit" to add your summary.'}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => setIsSummaryDialogOpen(true)}
              disabled={isSummaryLoading || isGeneratingSummary || isSavingSummary}
            >
              Edit Summary
            </Button>
            <Button
              variant="outlined"
              onClick={handleGenerateSummary}
              disabled={isGeneratingSummary || isSummaryLoading}
            >
              {isGeneratingSummary ? 'Generating...' : 'Generate Summary'}
            </Button>
          </Box>
        </Card>
      </Fade>

      {/* Faculty Interests Card wrapped in Fade */}
      <Fade in={fadeIn} timeout={500}>
        <Card elevation={3} sx={{ p: 2 }}>
          <Typography variant="h6">Faculty Interests</Typography>
          <List>
            {facultyInterests.map((interest, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <Button
                    onClick={() => handleRequestDeleteInterest(interest)}
                    color="error"
                    variant="outlined"
                  >
                    Delete
                  </Button>
                }
              >
                <ListItemText primary={interest} />
              </ListItem>
            ))}
          </List>
          {isAddingInterest ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
              <TextField
                value={newInterest}
                onChange={(e) => setNewInterest(e.target.value)}
                placeholder="Enter an interest..."
                fullWidth
                inputProps={{ maxLength: 50 }}
              />
              <Typography variant="caption" sx={{ alignSelf: 'flex-end' }}>
                {newInterest.length}/50 characters
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleSaveInterest} variant="contained" color="primary">
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setIsAddingInterest(false);
                    setNewInterest('');
                  }}
                  variant="outlined"
                  color="secondary"
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Button variant="contained" onClick={() => setIsAddingInterest(true)} sx={{ mt: 2 }}>
              Add Interest
            </Button>
          )}
        </Card>
      </Fade>

      {/* Publications Card wrapped in Fade */}
      <Fade in={fadeIn} timeout={500}>
        <Card elevation={3} sx={{ p: 2 }}>
          <Typography variant="h6">Publications</Typography>
          <List>
            {abstracts.map((abstract) => (
              <ListItem key={abstract.id} sx={{ flexWrap: 'wrap' }}>
                <ListItemText
                  primary={abstract.title}
                  secondary={
                    abstract.content.length > 100
                      ? `${abstract.content.substring(0, 100)}...`
                      : abstract.content
                  }
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    pr: 2,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: { xs: 1, sm: 0 },
                  }}
                >
                  <Button
                    onClick={() => {
                      setAbstractToEdit(abstract);
                      setIsEditAbstractModalOpen(true);
                    }}
                    variant="outlined"
                    color="primary"
                    sx={{ mb: { xs: 1, sm: 0 } }}
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => {
                      setPublicationToDelete(abstract);
                      setIsConfirmDeletePublicationOpen(true);
                    }}
                    color="error"
                    variant="outlined"
                  >
                    Delete
                  </Button>
                </Box>
              </ListItem>
            ))}
          </List>
          <Button
            variant="contained"
            onClick={() => setIsAbstractModalOpen(true)}
            sx={{ mt: 2 }}
          >
            Add Abstract
          </Button>
        </Card>
      </Fade>

      {/* Modals */}
      <EditSummaryModal
        open={isSummaryDialogOpen}
        onClose={() => setIsSummaryDialogOpen(false)}
        currentSummary={summary}
        onSave={handleSaveSummary}
      />
      <AddAbstractModal
        open={isAbstractModalOpen}
        onClose={() => setIsAbstractModalOpen(false)}
        onSave={handleAddAbstract}
      />
      {isEditAbstractModalOpen && abstractToEdit && (
        <EditAbstractModal
          open={isEditAbstractModalOpen}
          onClose={() => setIsEditAbstractModalOpen(false)}
          abstract={abstractToEdit}
          onSave={handleEditAbstract}
        />
      )}

      {/* Confirmation Dialog for deleting an interest */}
      <Dialog
        open={isConfirmDeleteOpen}
        onClose={handleCancelDeleteInterest}
        TransitionProps={{
          onExited: () => setInterestToDelete(null),
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the interest "{interestToDelete}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteInterest} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmDeleteInterest} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for deleting a publication */}
      <Dialog
        open={isConfirmDeletePublicationOpen}
        onClose={() => setIsConfirmDeletePublicationOpen(false)}
        TransitionProps={{
          onExited: () => setPublicationToDelete(null),
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the publication "{publicationToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDeletePublicationOpen(false)} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmDeletePublication} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop to block interactions when any operation is in progress */}
      <Backdrop
        open={isAnyOperationLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: '#fff',
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}

export default MyPortfolioTab;
